import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Header from '../Components/Header';
import { sendOtp, verifyOtp } from '../services/authService';
import { getProfile } from '../services/profileService';
import { saveAuthToken } from '../services/lineService';

const OTPContainer = styled.div`
  min-height: 100vh;
  background-color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
  }
`;

const OTPContent = styled.div`
  padding: 20px 16px 24px 16px;
  max-width: 400px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-top: 20px;

  @media (max-width: 480px) {
    max-width: 100%;
    padding: 12px 12px 20px 12px;
  }

  @media (max-height: 750px) {
    padding: 10px 12px 16px 12px;
  }

  @media (max-height: 650px) {
    padding: 8px 10px 12px 10px;
  }
`;

const OTPImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;

  @media (max-width: 480px) {
    margin-bottom: 12px;
  }

  @media (max-height: 700px) {
    margin-bottom: 10px;
  }

  @media (max-height: 650px) {
    margin-bottom: 8px;
  }
`;

const OTPImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;

  @media (max-width: 480px) {
    width: 90px;
    height: 90px;
  }

  @media (max-height: 700px) {
    width: 70px;
    height: 70px;
  }

  @media (max-height: 650px) {
    width: 60px;
    height: 60px;
  }
`;

const OTPTitle = styled.h1`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
  text-align: center;
  font-family: 'Noto Sans Thai', sans-serif !important;
  line-height: 1.4;
  word-break: break-word;
  padding: 0 8px;

  @media (max-width: 480px) {
    font-size: 16px;
    margin-bottom: 10px;
  }

  @media (max-height: 700px) {
    font-size: 15px;
    margin-bottom: 8px;
  }

  @media (max-height: 650px) {
    font-size: 14px;
    margin-bottom: 6px;
  }
`;

const OTPSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 8px;
  text-align: center;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 6px;
  }

  @media (max-height: 700px) {
    font-size: 13px;
    margin-bottom: 4px;
  }
`;

const RefCodeContainer = styled.div`
  background-color: #f0f9fb;
  border: 2px solid #00BBD3;
  border-radius: 12px;
  padding: 10px 16px;
  margin: 12px auto 20px auto;
  display: inline-block;
  max-width: 90%;

  @media (max-width: 480px) {
    padding: 8px 12px;
    margin: 8px auto 16px auto;
  }

  @media (max-height: 700px) {
    padding: 6px 12px;
    margin: 6px auto 12px auto;
  }
`;

const RefCodeLabel = styled.span`
  font-size: 14px;
  color: #666;
  margin-right: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;

  @media (max-width: 480px) {
    font-size: 13px;
    margin-right: 6px;
  }

  @media (max-height: 700px) {
    font-size: 12px;
  }
`;

const RefCodeValue = styled.span`
  font-size: 18px;
  font-weight: bold;
  color: #00BBD3;
  font-family: 'Noto Sans Thai', sans-serif !important;
  letter-spacing: 2px;

  @media (max-width: 480px) {
    font-size: 16px;
    letter-spacing: 1px;
  }

  @media (max-height: 700px) {
    font-size: 14px;
  }
`;

const ResendButtonContainer = styled.div`
  text-align: center;
  margin-bottom: 32px;

  @media (max-width: 480px) {
    margin-bottom: 24px;
  }

  @media (max-height: 700px) {
    margin-bottom: 16px;
  }
`;

const ResendButton = styled.button`
  background-color: ${props => props.disabled ? '#e1e5e9' : '#00BBD3'};
  color: ${props => props.disabled ? '#999' : 'white'};
  border: none;
  border-radius: 24px;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.disabled ? 'none' : '0 4px 8px rgba(0, 187, 211, 0.2)'};

  @media (max-width: 480px) {
    padding: 10px 24px;
    font-size: 14px;
  }

  @media (max-height: 700px) {
    padding: 8px 20px;
    font-size: 13px;
  }

  &:hover:not(:disabled) {
    background-color: #009bb3;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 187, 211, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const TimerText = styled.span`
  font-size: 14px;
  color: #666;
  margin-left: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;

  @media (max-width: 480px) {
    font-size: 12px;
    margin-left: 6px;
  }

  @media (max-height: 700px) {
    font-size: 11px;
  }
`;

const OTPForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 480px) {
    gap: 20px;
  }

  @media (max-height: 700px) {
    gap: 16px;
  }
`;

const OTPInputGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 0;
  max-width: 100%;
  padding: 0 10px;
  position: relative;
  cursor: text;

  @media (max-width: 400px) {
    gap: 6px;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    gap: 4px;
    padding: 0;
  }

  @media (max-height: 700px) {
    gap: 6px;
  }
`;

const HiddenOTPInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  font-size: 16px;
  z-index: 1;
  background: transparent;
  color: transparent;
  border: none;
  outline: none;
  caret-color: transparent;
  -webkit-text-fill-color: transparent;
  letter-spacing: -10px;
`;

const OTPBox = styled.div`
  width: 50px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid ${props => props.$error ? '#ff4444' : props.$active ? '#00BBD3' : '#e1e5e9'};
  border-radius: 12px;
  background-color: white;
  transition: all 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
  flex-shrink: 0;
  color: #333;
  box-shadow: ${props => props.$active && !props.$error ? '0 0 0 3px rgba(0, 187, 211, 0.1)' : 'none'};
  pointer-events: none;

  @media (max-width: 400px) {
    width: 40px;
    height: 50px;
    font-size: 20px;
    border-radius: 10px;
  }

  @media (max-width: 360px) {
    width: 36px;
    height: 46px;
    font-size: 18px;
    border-radius: 8px;
    border-width: 1.5px;
  }

  @media (max-width: 320px) {
    width: 32px;
    height: 42px;
    font-size: 16px;
    border-radius: 6px;
  }

  @media (max-height: 700px) {
    width: 42px;
    height: 52px;
    font-size: 20px;
  }
`;

const ConfirmButton = styled.button`
  background-color: #00BBD3;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 16px 32px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-shadow: 0 9px 14px 0 rgba(0, 187, 211, 0.08);
  margin-top: 8px;

  @media (max-width: 480px) {
    padding: 13px 28px;
    font-size: 16px;
    border-radius: 24px;
    margin-top: 6px;
  }

  @media (max-height: 700px) {
    padding: 11px 24px;
    font-size: 15px;
    margin-top: 4px;
  }

  @media (max-height: 650px) {
    padding: 10px 20px;
    font-size: 14px;
    margin-top: 2px;
  }

  &:hover:not(:disabled) {
    background-color: #009bb3;
  }

  &:active:not(:disabled) {
    background-color: #008aa0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #ff4444;
  font-size: 15px;
  text-align: center;
  margin: 0;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  display: ${props => props.$show ? 'block' : 'none'};
  word-break: break-word;
  padding: 0 8px;

  @media (max-width: 480px) {
    font-size: 14px;
  }

  @media (max-height: 700px) {
    font-size: 13px;
  }
`;

const OTPPage = () => {
  const navigate = useNavigate();
  const [otpValue, setOtpValue] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [refCode, setRefCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const hasSentOtp = useRef(false);
  const countdownInterval = useRef(null);
  const hiddenInputRef = useRef(null);

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      countdownInterval.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(countdownInterval.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [countdown]);

  // WebOTP API (Android) — ดัก OTP จาก SMS โดยตรง
  useEffect(() => {
    if (!('OTPCredential' in window)) return;
    const ac = new AbortController();
    navigator.credentials.get({
      otp: { transport: ['sms'] },
      signal: ac.signal,
    }).then((otp) => {
      if (otp?.code) {
        const digits = otp.code.replace(/\D/g, '').slice(0, 6);
        setOtpValue(digits);
        if (digits.length === 6) {
          setTimeout(() => {
            hiddenInputRef.current?.blur();
            const form = document.querySelector('form');
            if (form) form.requestSubmit();
          }, 100);
        }
      }
    }).catch(() => {});
    return () => ac.abort();
  }, []);

  // Native input event listener — จับ autofill ที่ bypass React onChange
  useEffect(() => {
    const input = hiddenInputRef.current;
    if (!input) return;
    const handleNativeInput = () => {
      const val = input.value.replace(/\D/g, '').slice(0, 6);
      if (val !== otpValue) {
        setOtpValue(val);
        setError(false);
        setErrorMessage('');
        if (val.length === 6) {
          setTimeout(() => {
            input.blur();
            const form = document.querySelector('form');
            if (form) form.requestSubmit();
          }, 100);
        }
      }
    };
    input.addEventListener('input', handleNativeInput);
    input.addEventListener('change', handleNativeInput);
    return () => {
      input.removeEventListener('input', handleNativeInput);
      input.removeEventListener('change', handleNativeInput);
    };
  }, [otpValue]);

  // Format countdown to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get phone number from API and send OTP on mount (only once)
  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasSentOtp.current) return;
    hasSentOtp.current = true;

    // Check if user has preAuthToken (token ชั่วคราวจากการ login ยังไม่ผ่าน OTP)
    const token = localStorage.getItem('preAuthToken');
    if (!token) {
      alert('กรุณาเข้าสู่ระบบอีกครั้ง');
      navigate('/');
      return;
    }

    // Always fetch profile to get the correct phone number for the logged-in user
    // This ensures we don't use a cached phone from a previous login
    fetchProfileAndSendOtp();
  }, []);

  const fetchProfileAndSendOtp = async () => {
    try {
      setLoading(true);
      const { getProfile } = await import('../services/profileService');
      const profileResponse = await getProfile();

      console.log('Profile response:', profileResponse);

      if (profileResponse.data?.tel || profileResponse.data?.phone) {
        const phone = profileResponse.data.tel || profileResponse.data.phone;
        localStorage.setItem('userPhone', phone);
        setPhoneNumber(phone);
        handleSendOtp(phone);
      } else {
        // No phone number available, use a default or ask user
        setError(true);
        setErrorMessage('ไม่พบเบอร์โทรศัพท์ในระบบ กรุณาติดต่อผู้ดูแลระบบ');
      }
    } catch (err) {
      console.error('Fetch profile error:', err);
      setError(true);
      setErrorMessage('ไม่สามารถดึงข้อมูลเบอร์โทรได้');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (phone) => {
    try {
      setLoading(true);
      const response = await sendOtp(phone || phoneNumber);

      console.log('OTP sent response:', response);

      if (response.status === 'success' || response.result) {
        setOtpSent(true);
        setError(false);
        setErrorMessage('');

        // Extract ref code from response
        if (response.ref || response.refCode || response.reference) {
          setRefCode(response.ref || response.refCode || response.reference);
        }

        // Start 5-minute countdown (300 seconds)
        setCountdown(300);
        setCanResend(false);
      } else {
        setError(true);
        setErrorMessage(response.message || 'ไม่สามารถส่ง OTP ได้');
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      setError(true);
      setErrorMessage('เกิดข้อผิดพลาดในการส่ง OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpValue(value);
    setError(false);
    setErrorMessage('');

    // Auto-submit เมื่อครบ 6 หลัก
    if (value.length === 6) {
      setTimeout(() => {
        hiddenInputRef.current?.blur();
        const form = document.querySelector('form');
        if (form) form.requestSubmit();
      }, 100);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otpValue;

    if (otpCode.length !== 6) {
      setError(true);
      setErrorMessage('กรุณากรอก OTP ให้ครบ 6 หลัก');
      return;
    }

    setLoading(true);
    setError(false);
    setErrorMessage('');

    try {
      const response = await verifyOtp(phoneNumber, otpCode, refCode);

      console.log('Verify OTP response:', response);

      // Check if verification successful - API returns token on success
      if (response.token || response.status === 'success' || response.result) {
        console.log('✅ OTP Verified! Loading user profile...');

        // Get user profile data
        let userData = null;
        try {
          const profileResponse = await getProfile();
          console.log('Profile response:', profileResponse);
          userData = profileResponse?.data || profileResponse?.profile || profileResponse;
        } catch (profileError) {
          console.error('Error loading profile:', profileError);
          // Continue to dashboard even if profile fails
        }

        // OTP สำเร็จแล้ว — เก็บ token จริงและ userData ลง storage
        // ใช้ token จาก OTP response ถ้ามี หรือใช้ preAuthToken
        const finalToken = response.token || localStorage.getItem('preAuthToken');
        saveAuthToken(finalToken, userData);

        // ลบ preAuthToken ออก (ไม่ต้องใช้แล้ว)
        localStorage.removeItem('preAuthToken');
        sessionStorage.removeItem('preAuthToken');

        // OTP verified successfully - navigate to dashboard
        console.log('✅ Navigating to dashboard...');
        navigate('/dashboard');
      } else {
        console.log('❌ OTP verification failed');
        setError(true);
        setErrorMessage(response.responseText || response.message || 'รหัส OTP ไม่ถูกต้อง');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(true);

      if (err.response?.data?.message) {
        setErrorMessage(err.response.data.message);
      } else if (err.response?.status === 401) {
        setErrorMessage('รหัส OTP ไม่ถูกต้อง');
      } else {
        setErrorMessage('เกิดข้อผิดพลาด กรุณาลองอีกครั้ง');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (!canResend) return;

    console.log('ส่ง OTP ใหม่');
    setOtpValue('');
    setError(false);
    setErrorMessage('');
    setRefCode('');
    handleSendOtp(phoneNumber);

    // Focus hidden input
    if (hiddenInputRef.current) hiddenInputRef.current.focus();
  };

  return (
    <OTPContainer>
      <Header />

      <OTPContent>
        <OTPImageContainer>
          <OTPImage src="./OTP.png" alt="OTP Verification" />
        </OTPImageContainer>

        <OTPTitle>
          กรุณากรอกรหัส OTP 6 หลัก
          <br />
          ที่ส่งทาง SMS ไปยัง{' '}
          {phoneNumber ? phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : ''}
        </OTPTitle>

        {refCode && (
          <RefCodeContainer>
            <RefCodeLabel>รหัสอ้างอิง:</RefCodeLabel>
            <RefCodeValue>{refCode}</RefCodeValue>
          </RefCodeContainer>
        )}

        <OTPSubtitle>หากยังไม่ได้รหัส SMS</OTPSubtitle>

        <ResendButtonContainer>
          <ResendButton
            type="button"
            onClick={handleResend}
            disabled={!canResend || loading}
          >
            {loading ? 'กำลังส่ง...' : 'ส่งรหัสใหม่'}
            {countdown > 0 && <TimerText>({formatTime(countdown)})</TimerText>}
          </ResendButton>
        </ResendButtonContainer>

        <OTPForm onSubmit={handleSubmit}>
          <OTPInputGroup onClick={() => hiddenInputRef.current?.focus()}>
            <HiddenOTPInput
              ref={hiddenInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={otpValue}
              onChange={handleOTPChange}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              autoComplete="one-time-code"
              autoFocus
            />
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <OTPBox
                key={index}
                $error={error}
                $active={isFocused && index === otpValue.length && otpValue.length < 6}
              >
                {otpValue[index] || ''}
              </OTPBox>
            ))}
          </OTPInputGroup>

          <ErrorMessage $show={error}>{errorMessage}</ErrorMessage>

          <ConfirmButton type="submit" disabled={loading || otpValue.length !== 6}>
            {loading ? 'กำลังตรวจสอบ...' : 'ยืนยัน'}
          </ConfirmButton>
        </OTPForm>
      </OTPContent>
    </OTPContainer>
  );
};

export default OTPPage;
